/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  ActForm,
  billingEngineQueries,
  getNameWithInitialsReversed,
  isAccountLoadedAndCustomerPaid,
  Organization,
  useCoordinator,
  UUID,
  AssigneesProjectResponseType,
} from '@builddocs/data-management'
import {
  AOSRType,
  getJSONAOSRById,
  getJSONSecondaryActById,
  SecondaryActType,
} from '@builddocs/filestore-api'
import { FeatureFlagContext, useToast } from '@builddocs/system'
import { Dialog, DialogContent, Toolbar } from '@mui/material'
import _ from 'lodash'
import { useQuery } from 'react-query'

import { updateAct } from './Actions'
import { ApplicationContent } from './ApplicationContent'
import FieldsEditor from './FieldsEditor'
import InteractiveActButtons from './InteractiveActButtons'
import { NotificationModal } from './NotificationModal'
import {
  AOSRJsonType,
  ListsFieldsType,
  SecondaryActJsonType,
} from './Types/dataJSON'
import { RowDataType } from './viewer/template.type'

const jsonNavigation = {
  supportingDocs: 'applications.supportingDocs',
  supportingDocsForWorks: 'documents_for_work.supportingDocsForWorks',
}

const pathJsonAOSR = (repository: string) =>
  jsonNavigation[repository] ?? `acceptance_object.completedWorks.${repository}`

type Props = {
  rights: {
    canEdit: boolean
    canInspect: boolean
    canSendActToSigning: boolean
    canSignAct: boolean
  }
  canShowSigningActions: boolean
  visible: boolean
  onClose: () => void
  act: AOSRType | SecondaryActType
  versionRemarkExon: number | null
  toReopenInspection: boolean
  onDeviate?: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
  ) => void
  onReload?: () => void
}

export const InteractiveActView: FC<Props> = (props: Props) => {
  const {
    rights,
    act,
    visible,
    versionRemarkExon,
    toReopenInspection,
    canShowSigningActions,
    onClose,
    onDeviate,
    onReload,
  } = props

  const [modalState, setModalState] = useState({
    open: false,
    message: '',
  })

  const { BUILDDOCS1941 } = useContext(FeatureFlagContext)
  const { contractId } = useCoordinator() as { contractId: string }
  const { toast } = useToast()

  const [zoom, setZoom] = useState(0.7)
  const [isUpdating, setIsUpdating] = useState(false)
  const [assigneesForActRegistry, setAssigneesForActRegistry] = useState<Record<
    string,
    UUID[]
  > | null>(act.assigneesForActRegistry)
  const [showCertAOSR, setShowCertAOSR] = useState<boolean>(
    'includeCertificatesInAttachments' in act
      ? act.includeCertificatesInAttachments
      : false,
  )
  const [forceIncludeOrganization, setForceIncludeOrganization] = useState<
    string[] | undefined
  >(
    'forceIncludeOrganization' in act
      ? act.forceIncludeOrganization
      : undefined,
  )
  const [shouldGenerateRegistry, setShouldGenerateRegistry] = useState<boolean>(
    'shouldCreateActRegistry' in act ? act.shouldCreateActRegistry : false,
  )

  const [shouldShowMaterials, setShouldShowMaterials] = useState<boolean>(
    'shouldShowMaterials' in act ? act.shouldShowMaterials : false,
  )

  const [shouldShowLaboratories, setShouldShowLaboratories] = useState<boolean>(
    'shouldShowLaboratories' in act ? act.shouldShowLaboratories : false,
  )

  const [shouldShowExecutiveSchemes, setShouldShowExecutiveSchemes] =
    useState<boolean>(
      'shouldShowExecutiveSchemes' in act
        ? act.shouldShowExecutiveSchemes
        : false,
    )

  const [
    shouldIncludeSupportingsInProjectDocuments,
    setShouldIncludeSupportingsInProjectDocuments,
  ] = useState<boolean>(
    'shouldIncludeSupportingsInProjectDocuments' in act
      ? act.shouldIncludeSupportingsInProjectDocuments
      : false,
  )
  const [additionalFields, setAdditionalFields] = useState<ListsFieldsType>({
    textFields: [],
    assigneesFields: [],
    organizationsFields: [],
    selectDocuments: [],
    selectMaterials: [],
    selectedWorks: [],
  })

  const [isFullScreen, setIsFullScreen] = useState(false)
  /** Получение JSON для вьер */
  const { isLoading, data } = useQuery(
    ['actJSON', act.id, act.actType],
    async () =>
      act.actType === ActForm.AOSR
        ? await getJSONAOSRById(act.id)
        : await getJSONSecondaryActById(act.id),
  )

  const [mode, setMode] = useState<'applications' | 'interactiveMode'>(
    'applications',
  )
  const [tools, setTools] = useState<'signatures' | 'comments' | 'edit' | null>(
    'signatures',
  )
  const [isDisabledTools, setIsDisabledTools] = useState(false)

  const handleChangeMode = (
    _event: React.MouseEvent<HTMLElement>,
    newState: 'applications' | 'interactiveMode' | null,
  ) => {
    const tools = BUILDDOCS1941
      ? rights.canEdit &&
        !(
          act?.approvalStatus === 'SIGNED' ||
          (act?.approvalStatus === 'SIGNING' &&
            !!act?.assignees?.find(a => a.signatureState === 'SIGNED'))
        )
        ? 'edit'
        : 'comments'
      : rights.canEdit
      ? 'edit'
      : 'comments'

    if (newState) {
      setMode(newState)
      newState === 'interactiveMode' && setTools(tools)
      newState === 'applications' && setTools('signatures')
    }
  }

  const handleChooseTools = (
    _event: React.MouseEvent<HTMLElement>,
    newState: 'signatures' | 'comments' | 'edit' | null,
  ) => newState && setTools(newState)

  const handleOffTools = (off: boolean) => {
    setIsDisabledTools(off)
    setTools(off ? null : 'signatures')
  }

  const handleChangeZoom = (e: 'inc' | 'dec' | number) => {
    const step = 0.1

    typeof e === 'number'
      ? e > 0 && setZoom(e)
      : setZoom(v => (e === 'inc' ? v + step : v > step ? v - step : v))
  }

  const handleGetField = useCallback(
    (fields: ListsFieldsType) => setAdditionalFields(fields),
    [],
  )

  useEffect(() => {
    setMode('applications')
    setTools('signatures')
    setZoom(0.7)
  }, [act])

  const initValue = useMemo(
    () => ({
      ...data,
      act_date: { value: act.actDate },
    }),
    [data, act],
  )

  const [dataJSON, setDataJSON] = useState<AOSRJsonType | SecondaryActJsonType>(
    initValue,
  )

  useEffect(() => setDataJSON(initValue), [initValue])

  const hasChanged = useMemo(() => {
    const isEditedGeneralData = !_.isEqual(dataJSON, initValue)

    const isEditedShowingCertificates =
      'includeCertificatesInAttachments' in act
        ? act.includeCertificatesInAttachments !== showCertAOSR
        : false

    const isEditedCreatingRegistry =
      'shouldCreateActRegistry' in act
        ? act.shouldCreateActRegistry !== shouldGenerateRegistry
        : false

    const isEditedShowingOrgInfo =
      'forceIncludeOrganization' in act &&
      Array.isArray(forceIncludeOrganization)
        ? !_.isEqual(
            _.sortBy(act.forceIncludeOrganization),
            _.sortBy(forceIncludeOrganization),
          )
        : false

    const isEditedAssigneesForActRegistry =
      'assigneesForActRegistry' in act &&
      !_.isEqual(act.assigneesForActRegistry, assigneesForActRegistry)

    const isEditedShouldIncludeSupportingsInProjectDocuments =
      'shouldIncludeSupportingsInProjectDocuments' in act &&
      !_.isEqual(
        act.shouldIncludeSupportingsInProjectDocuments,
        shouldIncludeSupportingsInProjectDocuments,
      )

    const isEditedShouldShowMaterials =
      'shouldShowMaterials' in act
        ? act.shouldShowMaterials !== shouldShowMaterials
        : shouldShowMaterials

    const isEditedShouldShowLaboratories =
      'shouldShowLaboratories' in act
        ? act.shouldShowLaboratories !== shouldShowLaboratories
        : shouldShowLaboratories

    const isEditedshouldShowExecutiveSchemes =
      'shouldShowExecutiveSchemes' in act
        ? act.shouldShowExecutiveSchemes !== shouldShowExecutiveSchemes
        : shouldShowExecutiveSchemes

    return (
      isEditedGeneralData ||
      isEditedShowingCertificates ||
      isEditedCreatingRegistry ||
      isEditedShowingOrgInfo ||
      isEditedAssigneesForActRegistry ||
      isEditedShouldIncludeSupportingsInProjectDocuments ||
      isEditedShouldShowMaterials ||
      isEditedShouldShowLaboratories ||
      isEditedshouldShowExecutiveSchemes
    )
  }, [
    dataJSON,
    initValue,
    showCertAOSR,
    shouldGenerateRegistry,
    forceIncludeOrganization,
    assigneesForActRegistry,
    act,
    shouldIncludeSupportingsInProjectDocuments,
    shouldShowMaterials,
    shouldShowLaboratories,
    shouldShowExecutiveSchemes,
  ])

  const handleForceIncludeOrganization = (key: string | string[]) => {
    if (Array.isArray(key)) {
      setForceIncludeOrganization(key)
    } else {
      setForceIncludeOrganization(prev =>
        prev
          ? prev.includes(key)
            ? prev.filter(_key => _key !== key)
            : [...prev, key]
          : [key],
      )
    }
  }

  const handleOrganization = (org: Organization | undefined, key: string) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        manualOrganization: org,
      },
    }))
  }

  const handleOrgToField = (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        manualOrganization: org,
      },
    }))
  }

  const handleOrgToFieldForAOSR = (
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
    key: string,
  ) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: org
          ? `${org.name}, ИНН ${org.inn}, ОГРН ${org?.ogrn}, ${org?.physicalAddress?.unrestrictedValue}`
          : '',
      },
    }))
  }

  const handleWork = (sectionIds: UUID[], key: string) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        selectedWorks: sectionIds,
      },
    }))
  }

  const handleMaterial = (materialIds: UUID[], key: string) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        selectedMaterials: materialIds,
      },
    }))
  }

  const handleAssigneesForAOSR = (
    signers: unknown[] | unknown,
    key: string,
  ) => {
    if (!Array.isArray(signers)) {
      return
    }

    setDataJSON(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        // убрать при добавлении фичи к вторичным актам
        manualAssignees: signers?.reduce(
          (acc, signer) =>
            signer.journalAssigneeId
              ? { ...acc, [signer.journalAssigneeId]: signer }
              : acc,
          {},
        ),
        overwrittenAssignees: signers?.reduce(
          (acc, signer) =>
            signer.journalAssigneeId
              ? { ...acc, [signer.journalAssigneeId]: signer }
              : acc,
          {},
        ),
      },
    }))
  }

  const handleAssignees = (
    signers: AssigneesProjectResponseType[],
    key: string,
    showPosition?: boolean,
  ) => {
    if (key.includes('_sign')) {
      setDataJSON(prev => ({
        ...prev,
        [key.replace('_sign', '')]: {
          ...prev[key],
          manualAssignees: signers?.reduce(
            (acc, signer) => ({ ...acc, [signer.employmentId]: signer }),
            {},
          ),
        },
        [key]: {
          value: signers
            .map(assignee =>
              showPosition
                ? `${
                    assignee.positionDescription
                  } ${getNameWithInitialsReversed(assignee)}`
                : getNameWithInitialsReversed(assignee),
            )
            .join(', '),
        },
      }))
    } else {
      setDataJSON(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          manualAssignees: signers?.reduce(
            (acc, signer) => ({ ...acc, [signer.employmentId]: signer }),
            {},
          ),
        },
        [`${key}_sign`]: {
          value: signers
            .map(assignee =>
              showPosition
                ? `${
                    assignee.positionDescription
                  } ${getNameWithInitialsReversed(assignee)}`
                : getNameWithInitialsReversed(assignee),
            )
            .join(', '),
        },
      }))
    }
  }

  const handleReloadAct = () => {
    if (onReload) {
      onReload()
    }
    onClose()
  }

  const handleChange = (value: string, address: string) => {
    setDataJSON(prev => _.setWith(_.clone(prev), address, value, _.clone))
  }

  const handleAddRowClick = (key: string, row: RowDataType[]) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        value: Array.isArray(prev[key]?.value)
          ? [...prev[key].value, row]
          : [row],
      },
    }))
  }

  const handleRemoveRowClick = (key: string, rowIndex: number) => {
    setDataJSON(prev => ({
      ...prev,
      [key]: {
        value: Array.isArray(prev[key]?.value)
          ? prev[key].value.filter(
              (_: RowDataType[], i: number) => i !== rowIndex,
            )
          : [],
      },
    }))
  }
  const handleRejectDocs = address => {
    setDataJSON(prev => ({
      ...prev,
      [address.key]: {
        ...prev[address.key],
        [address.field]: initValue[address.key][address.field],
      },
    }))
  }

  const handleSelectDoc = (
    docs: Record<string, any>[],
    repository: string,
    address?: string,
  ) => {
    if (dataJSON.act_type === ActForm.AOSR) {
      address &&
        setDataJSON(prev =>
          _.setWith(
            _.clone(prev),
            address,
            docs.map(doc => doc.id),
            _.clone,
          ),
        )

      setDataJSON(prev => {
        const data = _.get(prev, pathJsonAOSR(repository))

        docs.forEach(doc => !data[doc.id] && (data[doc.id] = doc))
        return _.setWith(_.clone(prev), pathJsonAOSR(repository), data, _.clone)
      })
    } else {
      setDataJSON(prev => {
        return _.setWith(_.clone(prev), repository, docs, _.clone)
      })
    }
  }

  const { data: oldActData } = useQuery(
    ['actJSON', act.id, act.actType],
    async () =>
      act.actType === ActForm.AOSR
        ? await getJSONAOSRById(act.id)
        : await getJSONSecondaryActById(act.id),
  )

  const { data: account } = useQuery(billingEngineQueries.getBillingAccount())
  const showWarning = isAccountLoadedAndCustomerPaid(account)

  const handleSaveWithCheck = () => {
    if (dataJSON.act_type === ActForm.AOSR && showWarning) {
      const laboratories =
        dataJSON.acceptance_object.completedWorks.aosrDocs.laboratoryIds

      const executiveSchemes =
        dataJSON.acceptance_object.completedWorks.aosrDocs.executiveSchemeIds

      const executiveDrawings =
        dataJSON.acceptance_object.completedWorks.aosrDocs.executiveDrawingIds

      const isSectionFourFilled =
        laboratories?.length ||
        executiveSchemes?.length ||
        executiveDrawings?.length

      const isSectionSevenFilled = !!dataJSON?.next_works?.value

      const conditions = [
        { isFilled: isSectionFourFilled, message: '4' },
        { isFilled: isSectionSevenFilled, message: '7' },
      ]

      if (conditions.some(condition => !condition.isFilled)) {
        setModalState({
          open: true,
          message: `Вы не заполнили п. ${conditions
            .filter(c => !c.isFilled)
            .map(c => c.message)
            .join(' и ')}`,
        })
        return
      }
    }

    handleSave()
  }

  const handleSave = async () => {
    if (window.confirm('Обновить акт, снять с проверки и подписания?')) {
      setIsUpdating(true)
      await updateAct(
        {
          contractId,
          actId: act.id,
          dataJSON,
          additionalFields,
          showCertAOSR,
          shouldGenerateRegistry,
          forceIncludeOrganization,
          versionRemarkExon,
          assigneesForActRegistry,
          shouldIncludeSupportingsInProjectDocuments,
          shouldShowMaterials,
          shouldShowLaboratories,
          shouldShowExecutiveSchemes,
        },
        { toast },
        oldActData,
      )
        .then(() => {
          onClose()
        })
        .catch(e => console.error(e))
        .finally(() => setIsUpdating(false))
    }
  }

  const handleReject = () => {
    setDataJSON(initValue)
    setShowCertAOSR(!!act['includeCertificatesInAttachments'])
    setShouldGenerateRegistry(act['shouldCreateActRegistry'])
    setForceIncludeOrganization(act['forceIncludeOrganization'])
    setAssigneesForActRegistry(act['assigneesForActRegistry'] ?? null)
    setShouldIncludeSupportingsInProjectDocuments(
      !!act['shouldIncludeSupportingsInProjectDocuments'],
    )
  }

  const handleClose = () => {
    handleReject()
    onClose()
  }

  const handleOnAddMaterials = (value: boolean) => {
    setShouldShowMaterials(value)
  }

  const handleOnAddLaboratories = (value: boolean) => {
    setShouldShowLaboratories(value)
  }

  const handleOnAddExecutiveSchemes = (value: boolean) => {
    setShouldShowExecutiveSchemes(value)
  }

  const handleOk = () => {
    handleSave()
    setModalState(state => ({ ...state, open: false }))
  }

  return (
    <>
      <Dialog
        fullWidth
        PaperProps={{
          sx: {
            height: isFullScreen ? '93vh' : '80vh',
            mx: isFullScreen ? 4 : undefined,
            borderRadius: isFullScreen ? 1 : undefined,
          },
        }}
        fullScreen={isFullScreen}
        maxWidth="xl"
        open={visible}
        onClose={handleClose}
      >
        <Toolbar sx={{ pt: 1 }} variant="dense">
          <InteractiveActButtons
            act={act}
            additionalFields={additionalFields}
            canEdit={rights.canEdit}
            canRegenerate={hasChanged && rights.canEdit}
            dataJSON={dataJSON}
            hasSignatures={!act.signatureEmbedded}
            isDisabledTools={isDisabledTools}
            isFullScreen={isFullScreen}
            mode={mode}
            tools={tools}
            zoom={zoom}
            onChangeMode={handleChangeMode}
            onChangeScreen={() => setIsFullScreen(prev => !prev)}
            onChangeZoom={handleChangeZoom}
            onChooseTools={handleChooseTools}
            onClose={handleClose}
            onReject={handleReject}
            onSave={handleSaveWithCheck}
          />
        </Toolbar>
        <DialogContent>
          {mode === 'interactiveMode' && dataJSON && (
            <FieldsEditor
              act={act}
              assigneesForActRegistry={assigneesForActRegistry}
              dataJSON={dataJSON}
              forceIncludeOrganization={forceIncludeOrganization}
              initValue={initValue}
              isLoading={isLoading || isUpdating}
              isOpenComments={tools === 'comments' || !rights.canEdit}
              isOpenEditor={tools === 'edit' && rights.canEdit}
              setShouldIncludeSupportingsInProjectDocuments={value =>
                setShouldIncludeSupportingsInProjectDocuments(value)
              }
              shouldGenerateRegistry={shouldGenerateRegistry}
              shouldIncludeSupportingsInProjectDocuments={
                shouldIncludeSupportingsInProjectDocuments
              }
              shouldShowExecutiveSchemes={shouldShowExecutiveSchemes}
              shouldShowLaboratories={shouldShowLaboratories}
              shouldShowMaterials={shouldShowMaterials}
              showCertAOSR={showCertAOSR}
              zoom={zoom}
              onAddExecutiveSchemes={handleOnAddExecutiveSchemes}
              onAddLaboratories={handleOnAddLaboratories}
              onAddMaterials={handleOnAddMaterials}
              onAddRowClick={handleAddRowClick}
              onChange={(value, address) => handleChange(value, address)}
              onDeviate={onDeviate}
              onEditRepresentativesInRegistry={setAssigneesForActRegistry}
              onGenerateRegistry={() =>
                setShouldGenerateRegistry((generate: boolean) => !generate)
              }
              onGetFields={handleGetField}
              onRejectDocs={address => handleRejectDocs(address)}
              onReloadAct={handleReloadAct}
              onRemoveRowClick={handleRemoveRowClick}
              onSelectAssignees={handleAssignees}
              onSelectAssigneesForAOSR={handleAssigneesForAOSR}
              onSelectDoc={handleSelectDoc}
              onSelectMaterial={handleMaterial}
              onSelectOrg={handleOrganization}
              onSelectOrgToField={handleOrgToField}
              onSelectOrgToFieldForAOSR={handleOrgToFieldForAOSR}
              onSelectShowInfoAboutOrg={handleForceIncludeOrganization}
              onSelectWork={handleWork}
              onShowCertAOSR={() => setShowCertAOSR((show: boolean) => !show)}
            />
          )}
          {mode === 'applications' && (
            <ApplicationContent
              act={act}
              canShowSigningActions={canShowSigningActions}
              isOpenComments={tools === 'comments'}
              isOpenSigning={tools === 'signatures'}
              rights={rights}
              toReopenInspection={toReopenInspection}
              onDeviate={onDeviate}
              onOffTools={handleOffTools}
            />
          )}
        </DialogContent>
      </Dialog>
      <NotificationModal
        {...modalState}
        fullWidth
        maxWidth="xs"
        onClose={() => setModalState(state => ({ ...state, open: false }))}
        onOk={handleOk}
      />
    </>
  )
}

export default InteractiveActView
